import { type ReactNode, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERMISSIONS,
  PRODUCT_STATUSES,
  type ActivateDraftsDto,
  type AdminProductListItem,
  type Locale,
  type ProductStatus,
} from '@aps/contracts';
import {
  Button,
  CategoryIcon,
  DataTable,
  EmptyState,
  Skeleton,
  formatMoney,
  useToast,
  type DataTableColumn,
} from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { can } from '../lib/session.js';
import { QueueError } from './WorkQueueScreen.js';

interface ProductsScreenProps {
  onOpenProduct: (productId: string) => void;
  onCreateProduct: () => void;
}

const PAGE_SIZE = 25;

/**
 * Holat yorliqlari. Ro'yxat KONTRAKTDAN — `Record` tipi tufayli enum'ga
 * yangi holat qo'shilsa va tarjimasi yozilmasa `tsc` yiqiladi.
 */
export const PRODUCT_STATUS_LABELS: Record<ProductStatus, Record<Locale, string>> = {
  DRAFT: { UZ: 'Qoralama', RU: 'Черновик' },
  ACTIVE: { UZ: 'Faol', RU: 'Активный' },
  ARCHIVED: { UZ: 'Arxiv', RU: 'Архив' },
};

/**
 * Qoralamalarni ommaviy faollashtirish paneli.
 *
 * Excel importi yangi tovarni `DRAFT` qiladi va Mini App uni ko'rsatmaydi.
 * Yuzlab tovarni bittalab ochib bo'lmaydi, shuning uchun tugma ekrandagi
 * FILTRGA mos hammasini ochadi. Ikki bosqich: bosishning o'zi darhol
 * mijozga chiqarib yubormasligi kerak.
 */
function ActivateDraftsBar({
  total,
  filter,
}: {
  total: number;
  filter: ActivateDraftsDto;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const activate = useMutation({
    mutationFn: () => api.activateDrafts(filter),
    onSuccess: (result) => {
      setConfirming(false);
      void client.invalidateQueries({ queryKey: ['admin-products'] });
      toast.show(
        t.productsActivated(result.activated, result.skipped, result.remaining),
        result.skipped > 0 || result.remaining > 0 ? 'info' : 'success',
      );
    },
    onError: (cause: unknown) => {
      setConfirming(false);
      toast.show(cause instanceof ApiError ? cause.text(locale) : t.errorTitle, 'danger');
    },
  });

  return (
    <div className="adm-bulk" role="region" aria-label={t.productsDraftsHint(total)}>
      <p className="adm-bulk__text">
        {confirming ? t.productsActivateConfirm(total) : t.productsDraftsHint(total)}
      </p>
      {confirming ? (
        <div className="adm-bulk__actions">
          <Button
            size="sm"
            disabled={activate.isPending}
            onClick={() => {
              activate.mutate();
            }}
          >
            {t.productsActivateYes}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={activate.isPending}
            onClick={() => {
              setConfirming(false);
            }}
          >
            {t.productsActivateNo}
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          onClick={() => {
            setConfirming(true);
          }}
        >
          {t.productsActivateAll(total)}
        </Button>
      )}
    </div>
  );
}

/**
 * Tovarlar ro'yxati.
 *
 * Qidiruv MIJOZNIKIDAN boshqa: u bazadan, imlo tuzatishsiz. Sabab —
 * Meilisearch indeksida faqat `ACTIVE` tovarlar bor, demak `DRAFT`
 * tovarni (Excel importidan keyin yuzlab shundaylari bo'ladi) qidiruv
 * orqali umuman topib bo'lmaydi.
 */
export function ProductsScreen({ onOpenProduct, onCreateProduct }: ProductsScreenProps): ReactNode {
  const { locale, me } = useSession();
  const t = messages(locale);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ProductStatus | null>(null);
  const [cursors, setCursors] = useState<string[]>([]);

  const cursor = cursors.at(-1);
  const trimmed = search.trim();

  const page = useQuery({
    queryKey: ['admin-products', trimmed, status, cursor ?? null],
    queryFn: () =>
      api.adminProducts({
        limit: PAGE_SIZE,
        ...(trimmed === '' ? {} : { query: trimmed }),
        ...(status === null ? {} : { status }),
        ...(cursor === undefined ? {} : { cursor }),
      }),
    placeholderData: keepPreviousData,
  });

  const resetPaging = (): void => {
    setCursors([]);
  };

  const header = (
    <header className="adm-page__header">
      <h1 className="adm-page__title">{t.navProducts}</h1>
      {can(me, PERMISSIONS.PRODUCT_CREATE) && (
        <Button size="sm" onClick={onCreateProduct}>
          {t.productNew}
        </Button>
      )}
    </header>
  );

  const filters = (
    <div className="adm-filters">
      <input
        className="aps-field__input adm-filters__search"
        type="search"
        placeholder={t.productSearch}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          resetPaging();
        }}
      />
      <div className="adm-filters__chips">
        <Button
          variant={status === null ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => {
            setStatus(null);
            resetPaging();
          }}
        >
          {t.ordersFilterAll}
        </Button>
        {PRODUCT_STATUSES.map((value) => (
          <Button
            key={value}
            variant={status === value ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => {
              setStatus(value);
              resetPaging();
            }}
          >
            {PRODUCT_STATUS_LABELS[value][locale]}
          </Button>
        ))}
      </div>
    </div>
  );

  if (page.isPending) {
    return (
      <section className="adm-page">
        {header}
        {filters}
        <Skeleton height="220px" label={t.loading} />
      </section>
    );
  }

  if (page.isError) {
    return (
      <section className="adm-page">
        {header}
        {filters}
        <QueueError
          message={page.error instanceof ApiError ? page.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void page.refetch()}
        />
      </section>
    );
  }

  const columns: DataTableColumn<AdminProductListItem>[] = [
    {
      key: 'sku',
      header: t.productSku,
      render: (row) => (
        <button
          className="adm-link"
          type="button"
          onClick={() => {
            onOpenProduct(row.id);
          }}
        >
          {row.sku}
        </button>
      ),
    },
    {
      key: 'name',
      header: t.productName,
      render: (row) => (
        <span className="adm-product-cell" data-cat={row.categoryIconKey}>
          {/*
            Kategoriya ikonkasi — demodagi ro'yxatda ham bor. U tovarni
            KO'Z bilan guruhlashga yordam beradi: uzun ro'yxatda matn
            o'qimasdan ham qaysi bo'lim ekani ko'rinadi.
          */}
          <span className="adm-product-cell__icon">
            <CategoryIcon iconKey={row.categoryIconKey} size={16} />
          </span>
          <span>
            {row.nameUz}
            <br />
            <span className="aps-muted">
              {row.brandName} · {row.categoryNameUz}
            </span>
          </span>
        </span>
      ),
    },
    {
      /*
       * Holat — ENG muhim ustun. Importdan keyin yuzlab tovar `DRAFT`
       * bo'lib tushadi va ular faol tovardan farqlanishi shart.
       */
      key: 'status',
      header: t.colStatus,
      render: (row) => (
        <span className={`adm-badge adm-badge--${row.status.toLowerCase()}`}>
          {PRODUCT_STATUS_LABELS[row.status][locale]}
        </span>
      ),
    },
    {
      key: 'price',
      header: t.colTotal,
      numeric: true,
      // "Tez kunda" tovarda narx 0 bo'lishi mumkin — "0 so'm" emas, belgi ko'rsatiladi.
      render: (row) =>
        row.comingSoon ? (
          <span className="adm-badge adm-badge--draft">{t.productComingSoonShort}</span>
        ) : (
          formatMoney(row.price, locale)
        ),
    },
    {
      key: 'stock',
      header: t.productStock,
      numeric: true,
      render: (row) =>
        row.supplyType === 'IN_STOCK'
          ? String(row.stockTotal)
          : t.productLeadTime(row.leadTimeDays),
    },
    {
      key: 'image',
      header: t.productImage,
      // Rasmsiz tovar mijozga kategoriya ikonkasi bilan chiqadi — bu
      // ishlaydi, lekin ro'yxatda ko'rinib tursin.
      render: (row) => (row.hasImage ? '✓' : <span className="aps-muted">—</span>),
    },
  ];

  return (
    <section className="adm-page">
      {header}
      {filters}

      {status === 'DRAFT' && page.data.total > 0 && can(me, PERMISSIONS.PRODUCT_EDIT) && (
        <ActivateDraftsBar
          total={page.data.total}
          filter={trimmed === '' ? {} : { query: trimmed }}
        />
      )}

      <DataTable
        caption={t.navProducts}
        columns={columns}
        rows={page.data.items}
        rowKey={(row) => row.id}
        empty={<EmptyState title={t.productsEmpty} hint={t.productsEmptyHint} />}
      />

      {page.data.nextCursor !== null && (
        <div className="adm-table-footer">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const next = page.data.nextCursor;
              if (next !== null) setCursors((current) => [...current, next]);
            }}
          >
            {t.loadMore}
          </Button>
        </div>
      )}
    </section>
  );
}
